import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

// UI Components
import Toast from '../../components/ui/Toast';

// Context Provider
import { PortfolioProvider, usePortfolio } from './context/PortfolioContext';

// Section Components
import ProfileHero from './components/ProfileHero';
import ProfileHeroSidebar from './components/ProfileHeroSidebar';
import AboutSection from './components/AboutSection';
import VideoIntroductionSection from './components/VideoIntroductionSection';
import SkillsSection from './components/SkillsSection';
import ExperienceTimeline from './components/ExperienceTimeline';
import PortfolioGallery from './components/PortfolioGallery';
import WorkStyleSection from './components/WorkStyleSection';
import ReviewsSection from './components/ReviewsSection';
import ProfileSEO from './components/ProfileSEO';

// Inner component that uses the context
const ProfileSidebarContent = () => {
  const navigate = useNavigate();
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const { profileData, completionPercentage } = usePortfolio();

  // Navigation handlers
  const handleEditProfile = () => {
    navigate('/va-profile-settings');
  };

  const handleShareProfile = () => {
    // Handle profile sharing functionality
    console.log('Profile shared');
    setToast({ show: true, message: 'Profile link copied to clipboard!', type: 'success' });
  };

  // These handlers would typically be moved to context in a full refactor
  const handleRequestQuote = (packageType) => {
    console.log('Quote requested for:', packageType);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* SEO Meta Tags - noindex for private view */}
      <ProfileSEO profile={profileData} isPublicView={false} />
      
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
      
      {/* Mobile Layout - Stacked */}
      <div className="lg:hidden">
        <ProfileHero 
          profile={profileData}
          onEditProfile={handleEditProfile}
          onShareProfile={handleShareProfile}
          completionPercentage={completionPercentage}
        />
        
        {/* Mobile Content Sections */}
        <div className="px-4 py-6 space-y-6">
          <AboutSection profile={profileData} />
          <VideoIntroductionSection profile={profileData} />
          <WorkStyleSection profile={profileData} />
          <SkillsSection profile={profileData} />
          <ExperienceTimeline profile={profileData} />
          <PortfolioGallery profile={profileData} />
          
          {profileData?.reviewCount > 0 && <ReviewsSection profile={profileData} />}
        </div>
      </div>

      {/* Desktop Resume-Style Layout */}
      <div className="hidden lg:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex gap-6">
            {/* Left Sidebar - Profile Hero */}
            <aside className="w-80 flex-shrink-0 sticky top-20 self-start">
              <ProfileHeroSidebar 
                profile={profileData}
                onEditProfile={handleEditProfile}
                onShareProfile={handleShareProfile}
                completionPercentage={completionPercentage}
              />
            </aside>

            {/* Main Content Area - Scrollable */}
            <main className="flex-1 min-w-0">
              <div className="space-y-6">
                <AboutSection profile={profileData} />
                <VideoIntroductionSection profile={profileData} />
                <WorkStyleSection profile={profileData} />
                <SkillsSection profile={profileData} />
                <ExperienceTimeline profile={profileData} />
                <PortfolioGallery profile={profileData} />
                
                {profileData?.reviewCount > 0 && <ReviewsSection profile={profileData} />}
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

const VAProfileSidebar = () => {
  const { user } = useAuth();

  return (
    <PortfolioProvider userId={user?.id}>
      <ProfileSidebarContent />
    </PortfolioProvider>
  );
};

export default VAProfileSidebar;
